import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  VALUE_MAX,
  buildFindingPayload,
  cleanValues,
  toValueList,
  validateFindingForm,
} from "./intraoralFindingForm";

const IntraoralFindingModal = ({ open, onClose, data, api, masterLabel }) => {
  const findingId = data?._id;
  const isUpdate = Boolean(findingId);

  const [name, setName] = useState("");
  const [values, setValues] = useState([]);
  const [valueHint, setValueHint] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setName(data?.name || "");
    setValues(toValueList(data?.values));
    setValueHint("");
  }, [open, data]);

  const validationError = useMemo(
    () => validateFindingForm({ name, values }),
    [name, values]
  );

  const handleValuesChange = (next) => {
    const { values: cleaned, duplicates } = cleanValues(next);
    setValues(cleaned);
    setValueHint(duplicates.length ? `"${duplicates[0]}" is already added.` : "");
  };

  const handleSubmit = async () => {
    if (validationError) return;

    setSaving(true);
    const payload = buildFindingPayload({ name, values });

    try {
      if (isUpdate) {
        await api.update(findingId, payload);
        toast.success("Finding updated successfully");
      } else {
        await api.create(payload);
        toast.success("Finding created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} finding`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose("close")}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isUpdate ? "Edit Finding" : "Add Finding"}
        {masterLabel && (
          <Box component="span" sx={{ display: "block", fontSize: "0.8rem", color: "#7D8DC4" }}>
            {masterLabel}
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <TextField
            label="Finding"
            required
            fullWidth
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ "data-testid": "finding-name" }}
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            size="small"
            value={values}
            onChange={(event, next) => handleValuesChange(next)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sub Values"
                placeholder="Type and press Enter"
                inputProps={{ ...params.inputProps, maxLength: VALUE_MAX }}
                error={Boolean(valueHint)}
                helperText={
                  valueHint || "Press Enter after each sub value (e.g. Good)."
                }
              />
            )}
          />

          {validationError && (
            <Box role="alert" sx={{ color: "#d32f2f", fontSize: "0.8rem" }}>
              {validationError}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "1rem 1.5rem" }}>
        <Button onClick={() => onClose("close")} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || Boolean(validationError)}
          sx={{ bgcolor: "#194AF5", ":hover": { bgcolor: "#1540d6" } }}
        >
          {saving ? <CircularProgress size="1.2rem" sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IntraoralFindingModal;
